/**
 * LeadConvertModal - Confirm conversion of a lead to a client
 */

import { useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { useLead, useConvertLead } from "../hooks/useLeads";

interface LeadConvertModalProps {
  leadId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onConvertComplete?: () => void;
}

export function LeadConvertModal({
  leadId,
  isOpen,
  onClose,
  onConvertComplete,
}: LeadConvertModalProps) {
  const [error, setError] = useState<string | null>(null);

  const { data: lead, isLoading } = useLead(leadId);
  const convertMutation = useConvertLead();

  const handleConvert = async () => {
    if (!leadId) return;

    setError(null);
    const result = await convertMutation.mutateAsync(leadId);

    if (result.success) {
      onConvertComplete?.();
      onClose();
    } else {
      setError(result.error || "Conversion failed");
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Convert Lead to Client"
      size="md"
    >
      <div className="p-6">
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
          </div>
        ) : lead ? (
          <>
            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                You are about to convert <span className="font-medium">{lead.name}</span> from a lead to a full client.
              </p>

              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-800 mb-2">
                  This will create:
                </h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    A new client record in the system
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Google Drive folders for content organization
                  </li>
                </ul>
              </div>

              {lead.instagramFollowers && (
                <div className="mt-4 text-sm text-gray-600">
                  <p>
                    Instagram baseline: <span className="font-medium">{lead.instagramFollowers.toLocaleString()}</span> followers
                  </p>
                  <p className="text-xs text-gray-500">
                    This will be saved as the starting point for growth tracking.
                  </p>
                </div>
              )}
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleConvert}
                disabled={convertMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {convertMutation.isPending ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Converting...
                  </span>
                ) : (
                  "Convert to Client"
                )}
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-8 text-gray-500">Lead not found</div>
        )}
      </div>
    </Modal>
  );
}
